/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */

import React, { useState } from 'react';

import { object as YupObject } from 'yup';
import { Formik } from 'formik';

import { useAuth0 } from '../../Auth0Provider';
import { useFlowControl } from '../../FlowControlProvider';

import PageContent from '../../theme/PageContent';
import PageHeader from '../../theme/PageHeader';
import UserTag from '../../theme/UserTag';
import ErrorBanner from '../../theme/ErrorBanner';
import BackButtonDesktop from '../../theme/BackButtonDesktop';

import { COUNTRY_CODE, FLOW_STEPS } from '../../../constants';

import InputValidator from '../../../services/input-validator';
import Api from '../../../services/api';
import Logger from '../../../services/logger';

import PhoneSignUpForm from './PhoneSignUpForm';

const gaScreenName = 'SignUp - Phone';

const validationSchema = YupObject().shape({
  name: InputValidator.nameValidation({
    emptyNameMsg: 'Your name must not be empty.',
    invalidNameMsg:
      'Your name must only contain letters, numbers, dots(.), hyphens(-) and slashes(/).',
    maxLengthMessage: 'Your name must be within 40 characters.',
  }),
  email: InputValidator.emailValidation({
    emptyEmailMessage: 'Email address is required.',
    invalidEmailMessage: 'Email address is not valid.',
    maxLengthMessage: 'Your email address should be within 64 characters.',
  }),
});

const PhoneSignup = () => {
  const [error, setError] = useState(null);
  const { userContext, updateUserContext, resetUserContext, passwordlessLogin, trackPwl } =
    useAuth0();
  const { reStart, goNext } = useFlowControl();

  const initialValues = {
    name: '',
    email: '',
    marketingConsentPreference: true,
  };

  const clearAndRestart = React.useCallback(() => {
    reStart();
    resetUserContext();
  }, [reStart, resetUserContext]);

  const handleMoveToEmailLogin = (email) => {
    updateUserContext({ email });
    goNext(FLOW_STEPS.EMAIL_LOGIN);
  };

  React.useEffect(
    () => {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Landed',
        event_label: 'SignUp_Phone',
      });
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onSubmit = async (values, actions) => {
    const { phoneNumber, verificationCode, authRef } = userContext;
    const { name, email, marketingConsentPreference } = values;

    try {
      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Click',
        event_label: 'SignUp_Phone_Submit',
      });

      const responseData = await Api.getEmailAvailability({ email });

      const { available } = responseData;

      if (available === false) {
        // show error if email is taken
        actions.setStatus({ emailAvailable: { [email]: false } });
        return;
      }

      if (available === true) {
        // sign up if email is not taken
        actions.setStatus({ emailAvailable: { [email]: true } });

        await Api.passwordlessSignup({
          phoneNumber,
          verificationCode,
          email,
          name,
          marketingConsentPreference,
          authRef, // default: undefined
        });

        trackPwl({
          screen_name: gaScreenName,
          event_action: 'Status',
          event_label: 'Signup_Success',
        });

        // complete passwordless authentication
        await new Promise((resolve) => {
          passwordlessLogin({ phoneNumber, verificationCode }, (err) => {
            if (err) {
              throw err;
            }
            resolve();
          });
        });
      } else {
        throw new Error('Request failed. Please try again.');
      }
    } catch (err) {
      setError(err);
      Logger.error(err);

      trackPwl({
        screen_name: gaScreenName,
        event_action: 'Message',
        event_label: 'SignUp_Phone_Fail',
      });
    }

    actions.setSubmitting(false);
  };

  const userTag = `+${COUNTRY_CODE} ${userContext.phoneNumber}`;

  return (
    <>
      <BackButtonDesktop action={clearAndRestart} />
      <PageContent>
        <PageHeader header="Create an account" backAction={clearAndRestart} />
        <UserTag content={userTag} data-testid="tag-userPhone" />
        {error && <ErrorBanner error={error} />}
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {(formikProps) => (
            <PhoneSignUpForm {...formikProps} handleMoveToEmailLogin={handleMoveToEmailLogin} />
          )}
        </Formik>
      </PageContent>
    </>
  );
};

export default PhoneSignup;
